import { useEffect, useState } from 'react';
import { fetchMarginParams, saveMarginParams, type MarginView } from '../bidService';
import { getBidUuid } from '../bidStore';
import { useFinancialVisible } from '../financialDisplay';

/**
 * Bid-level margin overrides (gap review 1.4): the editor for `GET/PUT /bids/<id>/margin-params`,
 * which shipped in P4 with zero frontend consumers — overrides were API-only.
 *
 * Money boundaries, same as everywhere: the route 403s a blind caller (surfaced here as the panel
 * simply not rendering — there is nothing to show, not something to grey out), and only
 * `profit_rate` reaches a BID total today. Most other fields persist for the WGL engine that
 * design §8 gates on business confirmation — they are shown and editable because an estimator
 * entering them now loses nothing when the engine lights up, but the panel says so rather than
 * implying they price anything yet.
 *
 * `overhead_on_revenue_pct` is the exception (Tier 4.1 rider, 2026-08-08): it moves no bid total
 * either, but it is NOT inert — it is the corporate overhead the converted project's daily P&L
 * deducts, seeded here from the company default at bid creation so a later change to that default
 * never reprices this bid. Entered here, consumed there; the note says exactly that.
 */
interface MarginPanelProps {
  bidId: string;
  /** Status edit-lock: overrides move the bid total, so a locked bid's margins are read-only
   * (the server refuses the PUT with a 409 regardless — this is the honest mirror). */
  locked?: boolean;
  /** Hands the fresh post-save view to the calculator cards (gap 4.1), which price from the
   * resolved margins — without it a new override would leave them holding a stale rate. */
  onSaved?: (next: MarginView) => void;
}

// TWO overheads, and they must never read as one (0024_pnl_overhead_per_bid.sql):
//   'Overhead %'            -> overhead_rate, the 2% COST overhead, answered `included` and inert
//   'Overhead on revenue %' -> overhead_on_revenue_pct, the corporate overhead the project's
//                              per-day P&L deducts (Tier 4.1 rider). It moves no BID total,
//                              which is why its hint says where it DOES land.
const FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: 'profit_rate', label: 'Profit %', hint: 'applied' },
  { key: 'overhead_rate', label: 'Overhead %' },
  { key: 'overhead_on_revenue_pct', label: 'Overhead on revenue %', hint: 'project P&L' },
  { key: 'markup_rate', label: 'Markup %' },
  { key: 'camp_rate', label: 'Camp $/man-day' },
  { key: 'load_rate', label: 'Load $' },
  { key: 'fluid_rate', label: 'Fluid $/m³' },
  { key: 'fluid_days', label: 'Fluid days' },
  { key: 'fill_factor', label: 'Fill factor' },
];

const MarginPanel = ({ bidId, locked = false, onSaved }: MarginPanelProps): JSX.Element | null => {
  const financialVisible = useFinancialVisible();
  const [view, setView] = useState<MarginView | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  /** What the server currently stores, as strings — the reference that makes CLEARING possible. */
  const [stored, setStored] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uuid = getBidUuid(bidId);

  useEffect(() => {
    let cancelled = false;
    if (!uuid || !financialVisible) return undefined;
    void fetchMarginParams(uuid).then((loaded) => {
      if (cancelled || !loaded) return;
      setView(loaded);
      const seeded: Record<string, string> = {};
      FIELDS.forEach(({ key }) => {
        const v = (loaded.margin_params as any)?.[key];
        seeded[key] = v === null || v === undefined ? '' : String(v);
      });
      setDraft(seeded);
      setStored(seeded);
    });
    return () => { cancelled = true; };
  }, [uuid, financialVisible]);

  // No render for a blind caller: the server refuses the data, so there is nothing to show.
  if (!financialVisible || !view) return null;

  const save = async (): Promise<void> => {
    if (!uuid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const patch: Record<string, number | null> = {};
      FIELDS.forEach(({ key }) => {
        const raw = draft[key] ?? '';
        if (raw === '') {
          // The endpoint's contract is EXPLICIT-NULL-CLEARS (margin_service's own docstring, the
          // opposite of the catalogs' COALESCE). A field the server stores that the user emptied
          // must SEND null — the first version skipped it, making every override un-clearable
          // from the UI and the header profit permanently inert once overridden (review, proven).
          if ((stored[key] ?? '') !== '') patch[key] = null;
          return;
        }
        const n = Number(raw);
        if (Number.isFinite(n) && raw !== (stored[key] ?? '')) patch[key] = n;
      });
      if (Object.keys(patch).length === 0) return; // nothing changed — the server 400s an empty PUT
      const next = await saveMarginParams(uuid, patch);
      setView(next);
      onSaved?.(next);
      const refreshed: Record<string, string> = {};
      FIELDS.forEach(({ key }) => {
        const v = (next.margin_params as any)?.[key];
        refreshed[key] = v === null || v === undefined ? '' : String(v);
      });
      setDraft(refreshed);
      setStored(refreshed);
    } catch (err: any) {
      setError(err?.message ?? 'Could not save margin overrides.');
    } finally {
      setSaving(false);
    }
  };

  const effectiveProfit = view.resolved?.effective?.profit_rate;
  const profitSource = view.resolved?.source?.profit_rate;

  return (
    <section className="bid-margin-panel">
      <h3 className="bid-items-title">Margins</h3>
      <p className="bid-margin-note">
        Effective profit: <strong>{effectiveProfit ?? '—'}%</strong>
        {profitSource ? ` (from ${profitSource.replace('_', ' ')})` : ''}. Only profit prices the
        bid today; overhead on revenue deducts in the converted project&apos;s daily P&amp;L, and
        the remaining parameters are stored for the extended engine.
      </p>
      <fieldset className="bid-locked-fieldset" disabled={locked}>
        <div className="bid-margin-grid">
          {FIELDS.map(({ key, label, hint }) => (
            <label key={key} className="bid-margin-field">
              {label}{hint ? ` (${hint})` : ''}
              <input
                type="number"
                value={draft[key] ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
        {locked ? (
          <p className="bid-margin-note">Margins are locked while the bid is a record.</p>
        ) : (
          <div className="pipeline-form-actions">
            <button className="bid-detail-back-btn" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save margins'}
            </button>
          </div>
        )}
      </fieldset>
      {error && <p className="pipeline-form-error">{error}</p>}
    </section>
  );
};

export default MarginPanel;
